import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";
import RichTextEditor from "components/form/RichTextEditor";

const BASE_URL = "https://api.kennapartners.com/api/v1";
const PracticeArea = () => {
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [filteredPracticeAreas, setFilteredPracticeAreas] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPracticeArea, setCurrentPracticeArea] = useState(null);
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [selectedInsightId, setSelectedInsightId] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // Added for search functionality

  const isInitialLoadRef = useRef(true); // Track if this is initial load
  const isSearchingRef = useRef(false); // Track if we're currently searching
  const allPracticeAreasCacheRef = useRef(null); // Cache all practice areas to avoid repeated API calls

  const [formData, setFormData] = useState({
    name: "",
    overview: { html: "", text: "" }, // Updated to object format
    key_contacts: [],
    featured_insights: [],
  });


   /* Pagination (exact pattern from Insights/News) */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage]  = useState(10);
  const [totalItems, setTotalItems]   = useState(0);



  useEffect(() => {
    fetchPracticeAreas();
    fetchProfiles();
    fetchInsights(); // Add this line
  }, [currentPage]); // Re-fetch practice areas when page changes

  // Initialize filteredPracticeAreas ONLY on initial load
  useEffect(() => {
    if (practiceAreas.length > 0 && isInitialLoadRef.current && !isSearchingRef.current) {
      setFilteredPracticeAreas(practiceAreas);
      isInitialLoadRef.current = false; // Mark that initial load is done
    }
  }, [practiceAreas]);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim()) {
      isSearchingRef.current = true;
      const debounceTimer = setTimeout(() => {
        searchAllPracticeAreas(searchTerm.trim());
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      // Search cleared - reset to current page
      isSearchingRef.current = false;
      setFilteredPracticeAreas(practiceAreas);
    }
  }, [searchTerm]);


 const fetchPracticeAreas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(
        `${BASE_URL}/practice-areas/?page=${currentPage}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const { data } = await res.json();

      const formatted = (data.practice_areas || []).map((area) => ({
        ...area,
        overview:
          typeof area.overview === "object"
            ? area.overview
            : { html: area.overview || "", text: area.overview || "" },
      }));
      setPracticeAreas(formatted);
      setFilteredPracticeAreas(formatted); // Initialize filtered practice areas
      setTotalItems(data.total_practice_area || 0);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to fetch practice areas");
      setPracticeAreas([]);
      setFilteredPracticeAreas([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const searchAllPracticeAreas = async (term) => {
    setLoading(true);
    try {
      let allPracticeAreas = allPracticeAreasCacheRef.current;

      // Only fetch if we don't have cached data
      if (!allPracticeAreas) {
        const token = localStorage.getItem("access_token");
        
        // Fetch ALL practice areas by looping through pages
        allPracticeAreas = [];
        let page = 1;
        let totalPracticeAreas = 0;

        // First, get the total count
        const firstResponse = await fetch(
          `${BASE_URL}/practice-areas/?page=1&limit=10`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!firstResponse.ok) throw new Error(`HTTP error! status: ${firstResponse.status}`);

        const firstData = await firstResponse.json();
        totalPracticeAreas = firstData.data?.total_practice_area || 0;
        
        const firstPageAreas = (firstData.data?.practice_areas || []).map((area) => ({
          ...area,
          overview:
            typeof area.overview === "object"
              ? area.overview
              : { html: area.overview || "", text: area.overview || "" },
        }));
        allPracticeAreas = [...firstPageAreas];

        // Calculate total pages needed
        const totalPages = Math.ceil(totalPracticeAreas / 10);

        // Fetch remaining pages
        for (let p = 2; p <= totalPages; p++) {
          const response = await fetch(
            `${BASE_URL}/practice-areas/?page=${p}&limit=10`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            console.warn(`Failed to fetch page ${p}`);
            continue;
          }

          const data = await response.json();
          const fetchedAreas = (data.data?.practice_areas || []).map((area) => ({
            ...area,
            overview:
              typeof area.overview === "object"
                ? area.overview
                : { html: area.overview || "", text: area.overview || "" },
          }));
          
          allPracticeAreas = [...allPracticeAreas, ...fetchedAreas];
        }
        
        // Cache the results for future searches
        allPracticeAreasCacheRef.current = allPracticeAreas;
      }

      // Filter from cache - this is instant!
      const filtered = allPracticeAreas.filter((area) => {
        const nameMatch = area?.name?.toLowerCase().startsWith(term.toLowerCase());
        return nameMatch; // ONLY return names that START WITH the search term
      });

      setFilteredPracticeAreas(filtered);
    } catch (error) {
      console.error("Error searching practice areas:", error);
      setFilteredPracticeAreas([]);
    } finally {
      setLoading(false);
    }
  };

const fetchInsights = async () => {
  try {
    const token = localStorage.getItem("access_token");
    let allInsights = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await fetch(`${BASE_URL}/insights/?page=${page}&limit=50`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const fetched = data.data?.insight || [];

      allInsights = [...allInsights, ...fetched];
      hasMore = fetched.length === 50;
      page += 1;
    }

    // Sort alphabetically
    allInsights.sort((a, b) => a.title.localeCompare(b.title));
    setInsights(allInsights);
  } catch (error) {
    console.error("Error fetching insights:", error);
    setError("Failed to fetch insights");
  }
};


  const handleInsightSelect = (e) => {
    setSelectedInsightId(e.target.value);
  };

  // Add this function to add an insight
  const addFeaturedInsight = () => {
    if (!selectedInsightId) return;

    const insightToAdd = insights.find((i) => i.id === selectedInsightId);
    if (!insightToAdd) return;

    if (formData.featured_insights?.some((fi) => fi.id === selectedInsightId)) {
      setError("This insight is already added");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      featured_insights: [
        ...(prev.featured_insights || []),
        { id: insightToAdd.id, title: insightToAdd.title },
      ],
    }));
    setSelectedInsightId("");
    setError("");
  };

  // Add this function to remove an insight
  const removeFeaturedInsight = (id) => {
    setFormData((prev) => ({
      ...prev,
      featured_insights: (prev.featured_insights || []).filter(
        (fi) => fi.id !== id
      ),
    }));
  };

const fetchProfiles = async () => {
  try {
    const token = localStorage.getItem("access_token");
    let allProfiles = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await fetch(`${BASE_URL}/profiles/?page=${page}&limit=50`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const fetched = data.data?.profiles || [];

      allProfiles = [...allProfiles, ...fetched];
      hasMore = fetched.length === 50; // keep going if current page is full
      page += 1;
    }

    // Sort alphabetically
    allProfiles.sort((a, b) => a.full_name.localeCompare(b.full_name));
    setProfiles(allProfiles);
  } catch (error) {
    console.error("Error fetching profiles:", error);
    setError("Failed to fetch profiles");
  }
};


  const fetchPracticeAreaDetails = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/practice-areas/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const practiceAreaData = data.data.practice_area || data.data;
      console.log(
        "Raw backend response:",
        JSON.stringify(practiceAreaData, null, 2)
      );
      console.log("Processed currentPracticeArea:", {
        ...practiceAreaData,
        overview: practiceAreaData.overview
          ? typeof practiceAreaData.overview === "object"
            ? practiceAreaData.overview
            : {
                html: practiceAreaData.overview || "",
                text: practiceAreaData.overview || "",
              }
          : { html: "", text: "" },
      });

      setCurrentPracticeArea({
        ...practiceAreaData,
        overview: practiceAreaData.overview
          ? typeof practiceAreaData.overview === "object"
            ? practiceAreaData.overview
            : {
                html: practiceAreaData.overview || "",
                text: practiceAreaData.overview || "",
              }
          : { html: "", text: "" },
        featured_insights: practiceAreaData.featured_insights || [],
        key_contacts: practiceAreaData.key_contacts || [],
      });

      setShowDetailsModal(true);
      setError("");
    } catch (error) {
      console.error("Error fetching practice area details:", error);
      setError("Failed to fetch practice area details");
      setCurrentPracticeArea(null);
    } finally {
      setLoading(false);
    }
  };

  const uploadFileToPracticeArea = async (id, file, token) => {
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await fetch(`${BASE_URL}/practice-areas/${id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "File upload failed");
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(selectedFile.type)) {
      setError("Please select a valid image file (JPG, JPEG, or PNG)");
      e.target.value = "";
      return;
    }
    if (selectedFile.size > 2097152) {
      setFileSizeError(`⚠️ "${selectedFile.name}" is ${(selectedFile.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller file.`);
      e.target.value = "";
      return;
    }
    setFileSizeError("");
    setFile(selectedFile);
  };

  const handleProfileSelect = (e) => {
    setSelectedProfileId(e.target.value);
  };

  const addKeyContact = () => {
    if (!selectedProfileId) return;

    const profileToAdd = profiles.find((p) => p.id === selectedProfileId);
    if (!profileToAdd) return;

    if (formData.key_contacts.some((kc) => kc.id === selectedProfileId)) {
      setError("This profile is already added as a key contact");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      key_contacts: [...prev.key_contacts, profileToAdd],
    }));
    setSelectedProfileId("");
    setError("");
  };

  const removeKeyContact = (id) => {
    setFormData((prev) => ({
      ...prev,
      key_contacts: prev.key_contacts.filter((kc) => kc.id !== id),
    }));
  };

  const handleCreatePracticeArea = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("Authentication required");

      if (!formData.name || !formData.overview.html) {
        // Changed this line
        throw new Error("Name and Overview are required");
      }

      const payload = {
        ...formData,
        key_contacts: formData.key_contacts.map((kc) => kc.id),
        featured_insights: formData.featured_insights?.map((fi) => fi.id) || [],
        overview: formData.overview, // Already in correct format
      };

      const createResponse = await fetch(`${BASE_URL}/practice-areas/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.message || "Creation failed");
      }

      const createData = await createResponse.json();
      const newPracticeArea = createData.data.practice_area;

      if (file) {
        try {
          const updatedData = await uploadFileToPracticeArea(
            newPracticeArea.id,
            file,
            token
          );
          setPracticeAreas((prev) => [
            ...prev.filter((pa) => pa.id !== newPracticeArea.id),
            updatedData,
          ]);
        } catch (uploadError) {
          console.error("Upload failed, keeping practice area:", uploadError);
          setPracticeAreas((prev) => [...prev, newPracticeArea]);
          setError("Created successfully but file upload failed");
        }
      } else {
        setPracticeAreas((prev) => [...prev, newPracticeArea]);
      }

      setShowCreateModal(false);
      resetForm();
      fetchPracticeAreas(); // Refresh list after creation
    } catch (error) {
      console.error("Create error:", error);
      setError(error.message || "Failed to create practice area");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePracticeArea = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("Authentication required");

      if (!formData.name || !formData.overview.html) {
        // Changed this line
        throw new Error("Name and Overview are required");
      }

      const payload = {
        ...formData,
        key_contacts: formData.key_contacts.map((kc) => kc.id),
        featured_insights: formData.featured_insights?.map((fi) => fi.id) || [],
        overview: formData.overview, // Already in correct format
      };
      const updateResponse = await fetch(
        `${BASE_URL}/practice-areas/${selectedId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.message || "Update failed");
      }

      const updateData = await updateResponse.json();
      let updatedPracticeArea = updateData.data.practice_area;

      if (file) {
        try {
          const fileUpdateData = await uploadFileToPracticeArea(
            selectedId,
            file,
            token
          );
          updatedPracticeArea = fileUpdateData;
        } catch (uploadError) {
          console.error("File upload failed:", uploadError);
          setError("Practice area updated but file upload failed");
        }
      }

      setPracticeAreas((prev) =>
        prev.map((area) =>
          area.id === selectedId ? updatedPracticeArea : area
        )
      );
      setShowEditModal(false);
      resetForm();
      fetchPracticeAreas(); // Refresh list after update
    } catch (error) {
      console.error("Update error:", error);
      setError(error.message || "Failed to update practice area");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePracticeArea = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Authentication required. Please login.");
      }

      const response = await fetch(`${BASE_URL}/practice-areas/${selectedId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      setPracticeAreas((prev) => prev.filter((area) => area.id !== selectedId));
      setShowDeleteModal(false);
      setSelectedId(null);
      setError("");
      // Reset to page 1 if we deleted the last item on the current page
      if (filteredPracticeAreas.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchPracticeAreas(); // Refresh current page if not moving back
      }
    } catch (error) {
      console.error("Error deleting practice area:", error);
      setError(error.message || "Failed to delete practice area");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (practiceArea) => {
    setFormData({
      name: practiceArea.name,
      overview: {
        html: practiceArea.overview?.html || practiceArea.overview || "",
        text: practiceArea.overview?.text || "",
      },
      key_contacts: practiceArea.key_contacts || [],
      featured_insights: practiceArea.featured_insights || [],
    });
    setSelectedId(practiceArea.id);
    setShowEditModal(true);
  };

  const openDeleteModal = (id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setSelectedId(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: "",
      overview: { html: "", text: "" },
      key_contacts: [],
      featured_insights: [],
    });
    setFile(null);
    setSelectedProfileId("");
    setSelectedInsightId("");
  };


    /* ------------------------ PAGINATION ------------------------ */
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex justify-between items-center mt-4 px-4 pb-4">
        <div>
          <p className="text-sm ">
            Showing{" "}
            <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> practice areas
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border"
          >
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 border rounded-md ${
                currentPage === page
                  ? ""
                  : ""
              }`}
            >
              {page}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };
  
  
 return (
    <main className="workspace">
      {fileSizeError && (
        <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",zIndex:9999,display:"flex",alignItems:"flex-start",gap:"12px",backgroundColor:"#dc2626",color:"white",fontSize:"14px",fontWeight:"500",padding:"16px 20px",borderRadius:"10px",boxShadow:"0 10px 40px rgba(0,0,0,0.35)",maxWidth:"500px",width:"calc(100% - 32px)"}}>
          <span style={{fontSize:"20px",lineHeight:"1",flexShrink:0}}>🚫</span>
          <div style={{flex:1,lineHeight:"1.5"}}>{fileSizeError}</div>
          <button onClick={()=>setFileSizeError("")} style={{marginLeft:"8px",background:"none",border:"none",color:"white",fontSize:"22px",lineHeight:"1",cursor:"pointer",flexShrink:0,fontWeight:"bold",padding:0}}>×</button>
        </div>
      )}
      <Breadcrumb title="Practice Areas">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Practice Areas</BreadcrumbItem>
      </Breadcrumb>

      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button className="float-right text-white" onClick={() => setError("")}>
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Practice Area Management</h1>
        <div className="flex gap-2">
          <button onClick={openCreateModal} className="btn-primary">
            <span className="la la-plus"></span> Add New Practice Area
          </button>
          <button
            onClick={fetchPracticeAreas}
            className="btn-secondary"
            disabled={loading}
          >
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-6"> {/* Added card for search and pagination */}
        <div className="card-body">
          <div className="flex gap-4 flex-wrap items-center">
            <div className="flex-1 min-w-64">
              <Input
                type="text"
                placeholder="Search practice areas by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !practiceAreas.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading practice areas...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                  <thead>
                    <tr>
                      <th className="w-1/3">Name</th>
                      <th className="w-1/3">Created Date</th>
                      <th className="w-1/3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPracticeAreas.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-8">
                          No practice areas found. Create your first practice area!
                        </td>
                      </tr>
                    ) : (
                      filteredPracticeAreas.map((area) => (
                        <tr key={area.id}>
                          <td>{area.name}</td>
                          <td>
                            {area.created_at
                              ? new Date(area.created_at).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td className="flex gap-1">
                            <Button
                              onClick={() => fetchPracticeAreaDetails(area.id)}
                              className="btn btn-sm btn-info"
                              title="View Details"
                            >
                              <span className="la la-eye"></span>
                            </Button>
                            <Button
                              onClick={() => openEditModal(area)}
                              className="btn btn-sm btn-warning"
                              title="Edit"
                            >
                              <span className="la la-edit"></span>
                            </Button>
                            <Button
                              onClick={() => openDeleteModal(area.id)}
                              color="danger"
                              className="btn btn-sm btn-danger"
                              title="Delete"
                            >
                              <span className="la la-trash"></span>
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

{/* Create Modal */}
{showCreateModal && (
  <div
    className="modal fade show fixed inset-0 overflow-y-auto"
    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    onClick={closeAllModals}
  >
    <div
      className="flex items-center justify-center min-h-screen px-4 !w-fit"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-dialog modal-lg relative w-full max-w-3xl mx-auto my-6">
        <div className="modal-content relative bg-white rounded-lg shadow-lg">
          <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
            <h5 className="modal-title text-xl font-semibold text-gray-800">
              Create New Practice Area
            </h5>
            <button
              onClick={closeAllModals}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
            >
              <span className="la la-times text-2xl"></span>
            </button>
          </div>
          
          <form onSubmit={handleCreatePracticeArea}>
            <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Basic Information
                </h3>

                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Overview *
                  </label>
                  <RichTextEditor
                    value={formData.overview.html}
                    onChange={(html) =>
                      setFormData((prev) => ({
                        ...prev,
                        overview: {
                          html,
                          text: html.replace(/<[^>]*>/g, ""), // Strip HTML tags for text version
                        },
                      }))
                    }
                    uploadEndpoint={`${BASE_URL}/practice-areas/editor/upload`}
                    showImageButton={false}
                  />
                </div>
              </div>

              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Key Contacts
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedProfileId}
                    onChange={handleProfileSelect}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a profile</option>
                    {profiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.full_name}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={addKeyContact}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </Button>
                </div>
                <div className="mt-2">
                  {formData.key_contacts.map((contact) => (
                    <span
                      key={contact.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                    >
                      {contact.full_name}
                      <button
                        type="button"
                        onClick={() => removeKeyContact(contact.id)}
                        className="ml-1 text-blue-600 hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Featured Insights
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedInsightId}
                    onChange={handleInsightSelect}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select an insight</option>
                    {insights.map((insight) => (
                      <option key={insight.id} value={insight.id}>
                        {insight.title}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={addFeaturedInsight}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </Button>
                </div>
                <div className="mt-2">
                  {formData.featured_insights?.map((insight) => (
                    <span
                      key={insight.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-1 mb-1"
                    >
                      {insight.title || `Insight ID: ${insight.id}`}
                      <button
                        type="button"
                        onClick={() => removeFeaturedInsight(insight.id)}
                        className="ml-1 text-purple-600 hover:text-purple-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Upload File
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-600
                    file:mr-4 file:py-2.5 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                  accept=".jpg,.jpeg,.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: JPG, JPEG, PNG
                </p>
              </div>
            </div>

            <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
              <Button
                type="submit"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating...
                  </span>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
)}

{/* Edit Modal */}
{showEditModal && (
  <div
    className="modal fade show fixed inset-0 overflow-y-auto"
    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    onClick={closeAllModals}
  >
    <div
      className="flex items-center justify-center min-h-screen px-4 !w-fit"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-dialog modal-lg relative w-full max-w-3xl mx-auto my-6 !w-fit">
        <div className="modal-content relative bg-white rounded-lg shadow-lg">
          <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
            <h5 className="modal-title text-xl font-semibold text-gray-800">
              Edit Practice Area
            </h5>
            <button
              onClick={closeAllModals}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
            >
              <span className="la la-times text-2xl"></span>
            </button>
          </div>
          
          <form onSubmit={handleUpdatePracticeArea} className="">
            <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Basic Information
                </h3>

                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Overview *
                  </label>
                  <RichTextEditor
                    value={formData.overview.html}
                    onChange={(html) =>
                      setFormData((prev) => ({
                        ...prev,
                        overview: {
                          html,
                          text: html.replace(/<[^>]*>/g, ""), // Strip HTML tags for text version
                        },
                      }))
                    }
                    uploadEndpoint={`${BASE_URL}/practice-areas/editor/upload`}
                    showImageButton={false}
                  />
                </div>
              </div>

              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Key Contacts
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedProfileId}
                    onChange={handleProfileSelect}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a profile</option>
                    {profiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.full_name}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={addKeyContact}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </Button>
                </div>
                <div className="mt-2">
                  {formData.key_contacts.map((contact) => (
                    <span
                      key={contact.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                    >
                      {contact.full_name}
                      <button
                        type="button"
                        onClick={() => removeKeyContact(contact.id)}
                        className="ml-1 text-blue-600 hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Featured Insights
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedInsightId}
                    onChange={handleInsightSelect}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select an insight</option>
                    {insights.map((insight) => (
                      <option key={insight.id} value={insight.id}>
                        {insight.title}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={addFeaturedInsight}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </Button>
                </div>
                <div className="mt-2">
                  {formData.featured_insights?.map((insight) => (
                    <span
                      key={insight.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-1 mb-1"
                    >
                      {insight.title || `Insight ID: ${insight.id}`}
                      <button
                        type="button"
                        onClick={() => removeFeaturedInsight(insight.id)}
                        className="ml-1 text-purple-600 hover:text-purple-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Upload File (Optional)
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-600
                    file:mr-4 file:py-2.5 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                  accept=".jpg,.jpeg,.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: JPG, JPEG, PNG
                </p>
                {formData.file_url && (
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">
                      Current file:{" "}
                    </span>
                    <a
                      href={formData.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      View File
                    </a>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="alert-danger mb-4 mx-6">
                {error}
                <button
                  className="float-right text-white"
                  onClick={() => setError("")}
                >
                  ×
                </button>
              </div>
            )}

            <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
              <Button
                type="submit"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating...
                  </span>
                ) : (
                  "Update Practice Area"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    onClick={closeAllModals}
                    className="btn-close text-red-500 dark:text-red-500 hover:text-red-700"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete this practice area? This
                    action cannot be undone.
                  </p>
                </div>
                <div className="modal-footer flex justify-center gap-4">
                  <button
                    onClick={closeAllModals}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                  >
                    Cancel
                  </button>
                  <Button
                    color="danger"
                    onClick={handleDeletePracticeArea}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete Practice Area"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-4xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    {currentPracticeArea?.name || "Practice Area Details"}
                  </h5>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <div className="modal-body p-6">
                  {loading ? (
                    <div className="text-center py-8">
                      <span className="la la-spinner la-spin text-2xl"></span>
                      <p className="mt-2">Loading details...</p>
                    </div>
                  ) : currentPracticeArea ? (
                    <div className="space-y-6">
                      {/* Basic Information Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h3 className="text-lg font-medium">
                            Basic Information
                          </h3>
                          <div className="space-y-2">
                            <p>
                              <span className="font-medium">Name:</span>{" "}
                              {currentPracticeArea.name || "N/A"}
                            </p>
                            <p>
                              <span className="font-medium">Created:</span>{" "}
                              {currentPracticeArea.created_at
                                ? new Date(
                                    currentPracticeArea.created_at
                                  ).toLocaleString()
                                : "N/A"}
                            </p>
                            <p>
                              <span className="font-medium">Last Updated:</span>{" "}
                              {currentPracticeArea.updated_at
                                ? new Date(
                                    currentPracticeArea.updated_at
                                  ).toLocaleString()
                                : "N/A"}
                            </p>
                            {currentPracticeArea.file_url && (
                              <p>
                                <span className="font-medium">File:</span>{" "}
                                <a
                                  href={currentPracticeArea.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:underline"
                                >
                                  View File
                                </a>
                              </p>
                            )}
                          </div>
                        </div>

                      </div>
                    {/* Overview Section */}
<div>
  <h3 className="text-lg font-medium mb-2">Overview</h3>
  <div 
    className="bg-gray-50 p-4 rounded"
    style={{
      maxHeight: "400px",
      overflowY: "auto",
      wordBreak: "break-word"
    }}
  >
    {currentPracticeArea.overview &&
    currentPracticeArea.overview.html ? (
      <div
        className="prose max-w-none"
        dangerouslySetInnerHTML={{
          __html: currentPracticeArea.overview.html,
        }}
      />
    ) : (
      <p className="text-gray-500">
        No overview available
      </p>
    )}
  </div>
</div>

                      {/* Key Contacts Section */}
                      {currentPracticeArea.key_contacts && (
                        <div className="mt-4">
                          <h3 className="text-lg font-medium mb-2">
                            Key Contacts (
                            {currentPracticeArea.key_contacts.length})
                          </h3>
                          {currentPracticeArea.key_contacts.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {currentPracticeArea.key_contacts.map(
                                (contact) => (
                                  <div
                                    key={contact.id}
                                    className="border p-3 rounded hover:bg-gray-50"
                                  >
                                    <p className="font-medium">
                                      {contact.full_name ||
                                        `Contact ID: ${contact.id}`}
                                    </p>
                                  </div>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-500">
                              No key contacts assigned
                            </p>
                          )}
                        </div>
                      )}

                      {/* Featured Insights Section */}
                      {currentPracticeArea?.featured_insights && (
                        <div className="mt-6">
                          <h3 className="text-lg font-medium mb-2">
                            Featured Insights (
                            {currentPracticeArea.featured_insights.length})
                          </h3>
                          {currentPracticeArea.featured_insights.length > 0 ? (
                            <div className="space-y-3">
                              {currentPracticeArea.featured_insights.map(
                                (insightRef) => {
                                  const fullInsight = insights.find(
                                    (i) => i.id === insightRef.id
                                  );
                                  return (
                                    <div
                                      key={insightRef.id}
                                      className="border p-4 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                      {fullInsight ? (
                                        <>
                                          <h4 className="font-medium text-blue-600">
                                            {fullInsight.title}
                                          </h4>
                                          {fullInsight.content &&
                                          fullInsight.content.text ? (
                                            <p className="text-sm text-gray-600 mt-1">
                                              {console.log(
                                                "Rendering insight content:",
                                                fullInsight.content
                                              )}
                                              {fullInsight.content.text.length >
                                              150
                                                ? `${fullInsight.content.text.substring(
                                                    0,
                                                    150
                                                  )}...`
                                                : fullInsight.content.text}
                                            </p>
                                          ) : (
                                            <p className="text-sm text-gray-600 mt-1">
                                              No content available
                                            </p>
                                          )}
                                          {fullInsight.date && (
                                            <p className="text-xs text-gray-500 mt-2">
                                              Published:{" "}
                                              {new Date(
                                                fullInsight.date
                                              ).toLocaleDateString()}
                                            </p>
                                          )}
                                          {fullInsight.authors?.length > 0 && (
                                            <div className="mt-2">
                                              <p className="text-xs font-medium text-gray-700">
                                                Authors:
                                              </p>
                                              <div className="flex flex-wrap gap-1 mt-1">
                                                {fullInsight.authors.map(
                                                  (author) => (
                                                    <span
                                                      key={author.id}
                                                      className="text-xs px-2 py-1 bg-gray-100 rounded"
                                                    >
                                                      {author.full_name}
                                                    </span>
                                                  )
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <p className="font-medium text-gray-600">
                                            Insight ID: {insightRef.id}
                                          </p>
                                          <p className="text-sm text-gray-500">
                                            Insight details not available
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-500">
                              No featured insights for this practice area
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-red-500">
                        Failed to load practice area details
                      </p>
                      <button
                        onClick={() => fetchPracticeAreaDetails(selectedId)}
                        className="mt-4 btn btn-primary"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
                <div className="modal-footer flex justify-end p-6 border-t">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="btn btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default PracticeArea;